import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, BRAND } from "../lib/bakeryData";

const FRANCHISE_LINKS = [
    { to: "/franchise#franchise-about", label: "About Franchise" },
    { to: "/franchise#franchise-advantages", label: "Advantage" },
    { to: "/franchise#franchise-form", label: "Fill Form" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname, hash } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1));
            if (el) el.scrollIntoView({ behavior: "smooth" });
        }
    }, [pathname, hash]);

    const isFranchiseActive = pathname.startsWith("/franchise");

    const renderLink = (l) => {
        if (!l.dropdown) {
            return (
                <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                        `text-sm tracking-wide uppercase transition-colors ${
                            isActive ? "text-[#C5832B]" : "text-[#3E2723] hover:text-[#C5832B]"
                        }`
                    }
                >
                    {l.label}
                </NavLink>
            );
        }
        return (
            <div key={l.to} className="relative group" data-testid="nav-link-franchise">
                <NavLink
                    to={l.to}
                    data-testid="nav-franchise-trigger"
                    className={`text-sm tracking-wide uppercase transition-colors inline-flex items-center gap-1 ${
                        isFranchiseActive ? "text-[#C5832B]" : "text-[#3E2723] hover:text-[#C5832B]"
                    }`}
                >
                    {l.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                </NavLink>
                <div
                    data-testid="franchise-dropdown"
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300"
                >
                    <div className="bg-white border border-[#E8D8C8] rounded-2xl shadow-[0_20px_50px_-20px_rgba(62,39,35,0.35)] py-2 min-w-[200px]">
                        {FRANCHISE_LINKS.map((s) => (
                            <Link
                                key={s.to}
                                to={s.to}
                                data-testid={`franchise-dropdown-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="block px-5 py-2.5 text-sm text-[#3E2723] hover:bg-[#F3EBE1] hover:text-[#C5832B] transition-colors"
                            >
                                {s.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-[#E8D8C8]/70 shadow-[0_1px_0_rgba(62,39,35,0.04)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="navbar-logo"
                    className="flex items-center group"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src={BRAND.logo}
                        alt="The Baker's Basket"
                        className="h-12 md:h-14 w-auto object-contain group-hover:scale-105 transition-transform"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-9">
                    {NAV_LINKS.map(renderLink)}
                </nav>

                <Link
                    to="/branches"
                    data-testid="navbar-order-cta"
                    className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-all hover:scale-[1.02]"
                >
                    Order Online
                </Link>

                <button
                    data-testid="navbar-mobile-toggle"
                    aria-label="Toggle menu"
                    className="md:hidden grid place-items-center w-10 h-10 rounded-full border border-[#E8D8C8] text-[#3E2723]"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="navbar-mobile-menu"
                    className="md:hidden bg-[#FAF8F5]/95 backdrop-blur-xl border-t border-[#E8D8C8]"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV_LINKS.map((l) => {
                            if (!l.dropdown) {
                                return (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `text-base font-serif ${
                                                isActive ? "text-[#C5832B]" : "text-[#3E2723]"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                );
                            }
                            return (
                                <div key={l.to} className="flex flex-col gap-3">
                                    <NavLink
                                        to={l.to}
                                        data-testid="mobile-nav-link-franchise"
                                        onClick={() => setOpen(false)}
                                        className={`text-base font-serif ${
                                            isFranchiseActive ? "text-[#C5832B]" : "text-[#3E2723]"
                                        }`}
                                    >
                                        {l.label}
                                    </NavLink>
                                    <div className="flex flex-col gap-2.5 pl-4 border-l-2 border-[#E8D8C8]">
                                        {FRANCHISE_LINKS.map((s) => (
                                            <Link
                                                key={s.to}
                                                to={s.to}
                                                onClick={() => setOpen(false)}
                                                data-testid={`mobile-franchise-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className="text-sm text-[#6D4C41]"
                                            >
                                                {s.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        <Link
                            to="/branches"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-order-cta"
                            className="mt-2 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm"
                        >
                            Order Online
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
